import React from 'react';
import { Check, Plus, X } from 'lucide-react';
import { useI18n } from '../../i18n/I18nContext';
import './AnswerOptions.css';

/**
 * A question's answer options, one row each: a letter to click to mark the row right, the option text
 * and a cross to remove it. The quiz editor and the exam editor's choice questions share it.
 *
 * Nothing is marked until the teacher clicks: a row is never right by default, and while none is the
 * list says what to do. `multiple` lets several rows be right (a second click unmarks one). Without
 * `onChangeOption` the rows are fixed answers (true / false) and the whole row is the button.
 */
export default function AnswerOptions({
  options,
  correct,
  letters,
  label,
  labelClassName,
  multiple = false,
  minOptions = 2,
  onToggleCorrect,
  onChangeOption,
  onAddOption,
  onRemoveOption,
}) {
  const { t } = useI18n();
  const fixed = !onChangeOption;
  const canAdd = !fixed && onAddOption && options.length < letters.length;
  const canRemove = !fixed && onRemoveOption && options.length > minOptions;
  const markLabel = (letter) => t('courseAssess.quizzes.markCorrectTooltip', { letter });

  return (
    <div className={`ao ${multiple ? 'ao--multiple' : ''}`.trim()}>
      <div className="ao-head">
        <span className={labelClassName}>{label}</span>
        {canAdd && (
          <button type="button" className="ds-btn ds-btn-ghost ds-btn-sm ao-add" onClick={onAddOption}>
            <Plus aria-hidden="true" />
            {t('courseAssess.quizzes.addOption')}
          </button>
        )}
      </div>
      {correct.length === 0 && (
        <p className="ao-hint">
          {t(fixed ? 'quizAuthoring.options.pickOneHint' : multiple ? 'quizAuthoring.options.pickCorrectManyHint' : 'quizAuthoring.options.pickCorrectHint')}
        </p>
      )}
      {options.map((opt, i) => {
        const letter = letters[i];
        const isCorrect = correct.includes(i);
        const rowClass = `ao-row ${isCorrect ? 'ao-row--correct' : ''}`.trim();
        if (fixed) {
          return (
            <button key={letter} type="button" className={`${rowClass} ao-row--fixed`} aria-pressed={isCorrect} onClick={() => onToggleCorrect(i)}>
              <span className="ao-letter" aria-hidden="true">{isCorrect && <Check size={16} />}</span>
              <span className="ao-text">{opt}</span>
            </button>
          );
        }
        return (
          <div key={letter} className={rowClass}>
            <button
              type="button"
              className="ao-mark hit-44"
              onClick={() => onToggleCorrect(i)}
              title={markLabel(letter)}
              aria-label={markLabel(letter)}
              aria-pressed={isCorrect}
            >
              <span className="ao-letter">{letter}</span>
            </button>
            <input
              type="text"
              className="ao-input"
              placeholder={t('courseAssess.quizzes.optionPlaceholder', { letter })}
              aria-label={t('courseAssess.quizzes.optionPlaceholder', { letter })}
              value={opt}
              onChange={(e) => onChangeOption(i, e.target.value)}
            />
            {canRemove && (
              <button
                type="button"
                className="ao-del"
                onClick={() => onRemoveOption(i)}
                aria-label={t('courseAssess.quizzes.removeOptionTooltip', { letter })}
                title={t('courseAssess.quizzes.removeOptionTooltip', { letter })}
              >
                <X size={14} aria-hidden="true" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
